import React, { useState, useCallback } from 'react';
import { RouteComponentProps } from '@reach/router';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import { FormikProps, withFormik, WithFormikConfig } from 'formik';
import { Text, HeaderText, Button, LinkButton, Icon } from '@el8/vital';

import { PatientMembershipPlanSubscriptionRaw } from 'EntityTypes';
import { Box } from 'components';
import { notify, formatDate, formatDollars } from 'utils';

import RootContainer from '../shared/RootContainer';
import PaymentsHeader from '../shared/PaymentsHeader';
import PaymentMethodForm, {
  PaymentMethodFormValues,
  paymentMethodInitialValues,
  paymentMethodValidationSchema,
} from '../shared/PaymentMethodForm';
import { useSaveCard } from '../shared/helpers';

type Values = PaymentMethodFormValues;

type NonFormikProps = RouteComponentProps<{ subscriptionId: string }>;

type Props = NonFormikProps & FormikProps<Values>;

const withFormikConfig: WithFormikConfig<NonFormikProps, Values> = {
  mapPropsToValues() {
    return paymentMethodInitialValues;
  },
  validationSchema: paymentMethodValidationSchema,
  handleSubmit() {
    // Required by Typescript, but we can't actually perform the submission here because
    // we can't access `stripe` (from useStripe) or `elements` (from useElements) inside of
    // this function. So instead, we handle submission inside the component.
  },
};

const PatientMembershipPage: React.FC<Props> = ({ isSubmitting }) => {
  const {
    membershipGroup,
    membershipPlan,
    membershipSubscription,
    patientId,
    patientKey,
    patientName,
    practiceDisplayName,
  } = window.elate;

  const [paymentMethod, setPaymentMethod] = useState(window.elate.paymentMethod);
  const [isEditing, setIsEditing] = useState(!paymentMethod);

  const stripe = useStripe();
  const elements = useElements();
  const isStripeInitialized = Boolean(stripe && elements);

  const handleEditStart = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleEditCancel = useCallback(() => {
    setIsEditing(false);
  }, []);

  const handleSave = useSaveCard(patientId, patientKey, (newPaymentMethod) => {
    notify('Payment method saved.');
    setPaymentMethod(newPaymentMethod);
    setIsEditing(false);
  });

  const amount =
    membershipSubscription.override_billing_amount_usd || membershipPlan.billing_amount_usd;
  const isFreeSubscription = Number(amount) === 0;
  const billingFrequency = membershipPlan.billing_frequency_display.toLowerCase();
  const groupMemberNames = membershipGroup?.membership_subscriptions
    .map((subscription: PatientMembershipPlanSubscriptionRaw) => {
      return `${subscription.patient.firstName} ${subscription.patient.lastName}`;
    })
    .join(', ');

  return (
    <RootContainer>
      <PaymentsHeader />
      {membershipGroup ? (
        <HeaderText
          align="center"
          style={{ margin: '0 auto 20px' }}
          legacyVariant="legacyHeaderText"
          tag="h1"
        >
          Membership Group Enrollment
        </HeaderText>
      ) : (
        <HeaderText
          align="center"
          style={{ margin: '0 auto 20px' }}
          legacyVariant="legacyHeaderText"
          tag="h1"
        >
          Membership Enrollment for
          <br />
          {patientName}
        </HeaderText>
      )}
      {!isFreeSubscription && (
        <Text tag="div" textSize="large" style={{ marginBottom: 20 }}>
          The membership fee is due {billingFrequency}. First payment is due{' '}
          {formatDate(membershipSubscription.start_date)}.
        </Text>
      )}
      <Text tag="div" textSize="large" weight="medium">
        Plan name
      </Text>
      <Text tag="div" textSize="large" style={{ marginBottom: 20 }}>
        {membershipPlan.display_name}
      </Text>

      <Text tag="div" textSize="large" weight="medium">
        Amount
      </Text>
      <Text tag="div" textSize="large" style={{ marginBottom: 20 }}>
        {isFreeSubscription ? '$0' : `${formatDollars(amount)} billed ${billingFrequency}`}
      </Text>
      {membershipGroup && (
        <React.Fragment>
          <Text tag="div" textSize="large" weight="medium">
            Members
          </Text>
          <Text tag="div" textSize="large" style={{ marginBottom: 20 }}>
            {groupMemberNames}
          </Text>
        </React.Fragment>
      )}
      <Text tag="div" textSize="large" weight="medium">
        Start date
      </Text>
      <Text tag="div" textSize="large" style={{ marginBottom: 20 }}>
        {formatDate(membershipSubscription.start_date)}
      </Text>

      {!isFreeSubscription && paymentMethod && (
        <>
          <Text tag="div" textSize="large" weight="medium">
            Payment method
          </Text>
          <Box direction="row" style={{ marginBottom: 24 }}>
            <Icon icon="credit-card" style={{ marginRight: 8, marginTop: 2 }} />
            <Text tag="div" textSize="large" style={{ flex: 1 }}>
              {paymentMethod.payment_method_description}
            </Text>
            <LinkButton
              disabled={isSubmitting}
              onClick={isEditing ? handleEditCancel : handleEditStart}
              text={isEditing ? 'Cancel update' : 'Update card'}
            />
          </Box>
        </>
      )}

      {!isFreeSubscription && isEditing && (
        <>
          <PaymentMethodForm />
          <Button
            disabled={!isStripeInitialized}
            kind="primary"
            loading={isSubmitting}
            onClick={handleSave}
            text="Save"
            style={{ marginBottom: 8, width: '100%' }}
          />
          <Text align="center" appearance="system" tag="div">
            By saving, you are agreeing to keep this card on file for {patientName} with{' '}
            {practiceDisplayName} (you will always be notified before any charges).
          </Text>
        </>
      )}

      {!isEditing && (
        <Text tag="div">
          If you have questions about this enrollment, please reach out directly to the practice.
        </Text>
      )}
    </RootContainer>
  );
};

export default withFormik(withFormikConfig)(PatientMembershipPage);
